import {
  Button,
  Checkbox,
  FluentProvider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  webLightTheme,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createOrderWorkFormRelationship,
  deleteOrderWorkFormRelationship,
  getOrderWorkFormRelationships,
  getPublishedFormMetadatas,
  type IOrderWorkFormRelationship,
} from '../../services/metadataService';

interface OrderWorkFormsPanelProps {
  orderFormDefinitionId?: number;
  tenantId?: number;
}

interface WorkFormOption {
  id: number;
  code: string;
  label: string;
  published: boolean;
}

const OrderWorkFormsPanel = ({
  orderFormDefinitionId,
  tenantId,
}: OrderWorkFormsPanelProps) => {
  const [workForms, setWorkForms] = useState<WorkFormOption[]>([]);
  const [relationships, setRelationships] = useState<
    IOrderWorkFormRelationship[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [orderFormIsPublished, setOrderFormIsPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const canLoad = Boolean(orderFormDefinitionId && tenantId);

  const loadData = useCallback(
    async (
      abortController = new AbortController(),
      syncSelection = true,
      clearError = true
    ) => {
      if (!orderFormDefinitionId || !tenantId) return;
      setLoading(true);
      if (clearError) setError(undefined);
      try {
        const [publishedOrders, publishedWorks, currentRelationships] =
          await Promise.all([
            getPublishedFormMetadatas(abortController, 'Order', tenantId),
            getPublishedFormMetadatas(abortController, 'Work', tenantId),
            getOrderWorkFormRelationships(
              abortController,
              orderFormDefinitionId,
              tenantId
            ),
          ]);
        if (abortController.signal.aborted) return;

        const publishedOrderIds = new Set(
          publishedOrders
            .map((form) => form.metadata.formDefinitionId)
            .filter((id): id is number => typeof id === 'number')
        );
        setOrderFormIsPublished(publishedOrderIds.has(orderFormDefinitionId));

        const options = new Map<number, WorkFormOption>();
        for (const form of publishedWorks) {
          const id = form.metadata.formDefinitionId;
          if (typeof id !== 'number') continue;
          options.set(id, {
            id,
            code: String(id),
            label: form.name,
            published: true,
          });
        }
        for (const relationship of currentRelationships) {
          if (!options.has(relationship.workFormDefinitionId)) {
            options.set(relationship.workFormDefinitionId, {
              id: relationship.workFormDefinitionId,
              code: relationship.workFormCode,
              label: relationship.workFormLabel,
              published: relationship.publishedMetadataId != null,
            });
          }
        }
        setWorkForms(
          [...options.values()].sort((left, right) =>
            left.label.localeCompare(right.label)
          )
        );
        setRelationships(currentRelationships);
        if (syncSelection) {
          setSelectedIds(
            currentRelationships.map((item) => item.workFormDefinitionId)
          );
        }
      } catch (loadError: unknown) {
        if (abortController.signal.aborted) return;
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load related Work forms.'
        );
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    },
    [orderFormDefinitionId, tenantId]
  );

  useEffect(() => {
    if (!canLoad) return;
    const abortController = new AbortController();
    void loadData(abortController);
    return () => abortController.abort();
  }, [canLoad, loadData]);

  const linkedIds = useMemo(
    () => new Set(relationships.map((item) => item.workFormDefinitionId)),
    [relationships]
  );

  const save = async () => {
    if (!orderFormDefinitionId || !tenantId) return;
    const toAdd = selectedIds.filter((id) => !linkedIds.has(id));
    const toRemove = [...linkedIds].filter((id) => !selectedIds.includes(id));
    if (toAdd.length && !orderFormIsPublished) {
      setError(
        'Publish this Order form and each selected Work form before creating relationships.'
      );
      return;
    }

    setSaving(true);
    setError(undefined);
    const abortController = new AbortController();
    try {
      for (const workFormDefinitionId of toAdd) {
        await createOrderWorkFormRelationship(
          abortController,
          orderFormDefinitionId,
          workFormDefinitionId,
          tenantId
        );
      }
      for (const workFormDefinitionId of toRemove) {
        await deleteOrderWorkFormRelationship(
          abortController,
          orderFormDefinitionId,
          workFormDefinitionId,
          tenantId
        );
      }
      await loadData(abortController);
    } catch (saveError: unknown) {
      const message =
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save related Work forms.';
      await loadData(abortController, false, false);
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!tenantId) {
    return (
      <MessageBar>
        <MessageBarBody>
          Select a tenant before managing related Work forms.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (!orderFormDefinitionId) {
    return (
      <MessageBar>
        <MessageBarBody>
          Save this Order form to create its stable form identity before
          configuring related Work forms.
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Text>
          Select the published Work forms permitted for Orders using this form.
          Relationships are environment-local and are not included in form
          exports or imports.
        </Text>
        {!loading && !error && !orderFormIsPublished && (
          <MessageBar intent="warning">
            <MessageBarBody>
              Publish this Order form and each Work form first. Relationship
              creation is available only when both forms have a published
              version.
            </MessageBarBody>
          </MessageBar>
        )}
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        <Text>
          If relationship creation is rejected because a form is unpublished,
          publish both forms and try again.
        </Text>
        {loading ? (
          <Spinner label="Loading tenant Work forms" />
        ) : workForms.length ? (
          <div
            role="group"
            aria-label="Related Work forms"
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {workForms.map((form) => (
              <Checkbox
                key={form.id}
                label={
                  <span>
                    {form.label} — form identity {form.code}
                    {!form.published ? ' (no current published version)' : ''}
                  </span>
                }
                checked={selectedIds.includes(form.id)}
                disabled={
                  saving ||
                  (!form.published && !linkedIds.has(form.id)) ||
                  (!orderFormIsPublished && !linkedIds.has(form.id))
                }
                onChange={(_event, data) => {
                  setSelectedIds((current) =>
                    data.checked
                      ? [...current, form.id]
                      : current.filter((id) => id !== form.id)
                  );
                }}
              />
            ))}
          </div>
        ) : !error ? (
          <Text>
            No published Work forms are available for this tenant. Publish the
            Work forms before linking them.
          </Text>
        ) : null}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            appearance="primary"
            disabled={loading || saving || !!error}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save related forms'}
          </Button>
          <Button
            appearance="secondary"
            disabled={loading || saving}
            onClick={() => void loadData()}
          >
            Refresh
          </Button>
        </div>
      </div>
    </FluentProvider>
  );
};

export default OrderWorkFormsPanel;
